import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { FormProvider, useWatch } from 'react-hook-form'
import { ConsignmentItemAdditionalSupplyChainActors } from 'features/declaration/form/components/AdditionalSupplyChainActors'
import { ConsignmentItemFormValues } from 'features/declaration/consignment-item/models/schema'
import SupportingDocuments from 'features/declaration/form/components/SupportingDocuments'
import PreviousDocuments from 'features/declaration/form/components/PreviousDocuments'
import { blankPreviousDocument, blankSupportingDocument } from 'features/declaration/models/blankItems'
import { HandleSubmitResult } from 'features/declaration/form/components/TransitFormTabHandle'
import CollapsibleFormGroup from '../../../../../../components/p5/CollapsibleFormGroup'
import useConsignmentItem from '../../../../consignment-item/components/useConsignmentItem'
import TransitGoodsData from './TransitGoodsData'
import TransitPassiveBorderTransportMeans from './TransitPassiveBorderTransportMeans'
import { toConsignmentItemFormValues } from '../../../../consignment-item/models/mapper'
import useConsignmentItemForm, { ConsignmentItemFormSaveResult } from '../../../../consignment-item/components/useConsignmentItemForm'
import Packaging from '../../../../consignment-item/components/Packaging'
import AdditionalReferences from '../../../../consignment-item/components/AdditionalReferences'
import { useDeclarationEditorContext } from '../../../contexts/DeclarationEditorContext'
import LoadingOverlay from '../../../../../../components/p5/LoadingOverlay'
import useScopedServerRequirements from '../../../validations/useScopedServerRequirements'
import useNctsPhase from '../../../components/useNctsPhase'
import useDeclarationCountry from '../../../components/useDeclarationCountry'
import { notifyConsignmentItemSaveResult } from '../../../../consignment-item/components/notifyConsignmentItemSaveResult'
import applyFormFieldErrors, { FieldError } from '../../../../../../shared/rhf/applyFormFieldErrors'

export interface ConsignmentItemFormHandle {
  save: () => Promise<ConsignmentItemFormSaveResult>
  isDirty: () => boolean
}

interface ConsignmentItemFormProps {
  id: number
  houseConsignmentId: number
  onSaveSuccess?: () => void
  fieldErrors?: FieldError[]
}

const TransitConsignmentItemForm = forwardRef<ConsignmentItemFormHandle, ConsignmentItemFormProps>(({
  id,
  houseConsignmentId,
  onSaveSuccess,
  fieldErrors,
}, ref) => {
  const { t } = useTranslation()

  const { declarationData, editorData } = useDeclarationEditorContext()
  const { consignment, transitOperation } = declarationData

  if (!transitOperation?.id) throw new Error('Missing transit operation ID')
  if (!consignment?.id) throw new Error('Missing consignment ID')

  const transitOperationId = transitOperation.id
  const { phase, isOptIn } = useNctsPhase()
  const [isSaving, setIsSaving] = useState(false)
  const [isFormPopulated, setIsFormPopulated] = useState(false)
  const [isCleanBaselineSet, setIsCleanBaselineSet] = useState(false)

  const country = useDeclarationCountry()

  const {
    consignmentItem, consignmentItemDocuments, additionalReferences, isLoading, saveConsignmentItemFormValues,
  } = useConsignmentItem({
    id,
    consignmentId: consignment.id,
    transitOperationId: transitOperation.id,
    houseConsignmentId,
  })

  const { form } = useConsignmentItemForm()
  const {
    reset, handleSubmit, formState: { isDirty },
  } = form

  // Open the (otherwise collapsed) Transportation group on load when the item
  // already carries border transport means.
  const passiveBorderTransportMeans = useWatch({ control: form.control, name: 'passiveBorderTransportMeans' })
  const hasTransportData = (passiveBorderTransportMeans?.length ?? 0) > 0

  const { isLoading: isServerRequirementsLoading } = useScopedServerRequirements('consignmentItem', {
    transitOperationId,
    phase,
    isOptIn,
    houseConsignmentId,
    consignmentItemId: id,
  }, { enabled: isFormPopulated })

  useEffect(() => {
    // Populate once on initial mount. Re-running this effect after a failed
    // save (via isSaving deps) used to spuriously clear isDirty even with
    // keepDirtyValues: true, breaking the drawer's save-on-close guard on
    // the second attempt. After population, save() handles its own reset on
    // success; the form owns its dirty state in between.
    if (isFormPopulated) return
    if (!consignmentItem || !consignmentItemDocuments || !additionalReferences) return

    const newFormValues = toConsignmentItemFormValues(false, consignmentItem, consignmentItemDocuments, additionalReferences)
    reset({
      ...newFormValues,
      packaging: Array.isArray(newFormValues.packaging) ? newFormValues.packaging : [],
    })
    setIsFormPopulated(true)
  }, [reset, consignmentItem, consignmentItemDocuments, additionalReferences, isFormPopulated])

  useEffect(() => {
    if (!isFormPopulated || !fieldErrors) return
    applyFormFieldErrors(form, fieldErrors)
  }, [isFormPopulated, fieldErrors, form])

  useEffect(() => {
    if (isCleanBaselineSet || !isFormPopulated || isLoading || isServerRequirementsLoading) return
    reset(form.getValues(), { keepErrors: true })
    setIsCleanBaselineSet(true)
  }, [isCleanBaselineSet, isFormPopulated, isLoading, isServerRequirementsLoading, reset, form])

  const validateAndGet = useCallback(() => new Promise<HandleSubmitResult<ConsignmentItemFormValues>>((resolve) => {
    handleSubmit(
      (data) => {
        resolve({ success: true, data })
      },
      (formErrors) => {
        resolve({ success: false, formErrors })
      },
    )()
  }), [handleSubmit])

  const save = useCallback(async (): Promise<ConsignmentItemFormSaveResult> => {
    setIsSaving(true)

    try {
      const result = await validateAndGet()
      if (!result.success) {
        // eslint-disable-next-line no-console
        console.error('consignment item form errors', result.formErrors)
        return { success: false, errorReason: 'validation' }
      }

      if (!result.data) {
        return { success: false, errorReason: 'general', errors: [{ message: 'Data missing' }] }
      }

      const { data: saved, errors } = await saveConsignmentItemFormValues({
        values: result.data,
        isDraft: true, // always draft
      })

      if (errors.length) {
        return { success: false, errorReason: 'general', errors }
      }

      // Rebase the form on the just-saved server state.
      form.reset(toConsignmentItemFormValues(false, saved.consignmentItem, saved.consignmentItemDocuments, saved.additionalReferences))

      onSaveSuccess?.()
      return { success: true, data: saved }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error saving consignment item', e)
      return { success: false, errorReason: 'unexpected', error: e }
    } finally {
      setIsSaving(false)
    }
  }, [form, onSaveSuccess, saveConsignmentItemFormValues, validateAndGet])

  useImperativeHandle(ref, () => ({
    save,
    isDirty: () => isDirty,
  }))

  const handleFormSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault()
    const result = await save()
    notifyConsignmentItemSaveResult(result, t)
  }

  return (
    <LoadingOverlay loading={isLoading || isServerRequirementsLoading || isSaving}>
      <FormProvider {...form}>
        <form onSubmit={handleFormSubmit}>
          <div className="hc-form">
            <div className="row">
              <div className="col-lg-8">
                <TransitGoodsData />
              </div>
              <div className="col-lg-4">
                <Packaging />
                <ConsignmentItemAdditionalSupplyChainActors />
              </div>
            </div>

            <CollapsibleFormGroup title={t('declaration.p5.consignmentItemDrawer.documents')}>
              <div className="row">
                <div className="col-lg-4">
                  <SupportingDocuments<ConsignmentItemFormValues>
                    scope="consignmentItem"
                    createDefault={() => blankSupportingDocument}
                    isEns={false}
                    country={country}
                  />
                </div>
                <div className="col-lg-4">
                  <PreviousDocuments<ConsignmentItemFormValues>
                    scope="consignmentItem"
                    createDefault={() => blankPreviousDocument}
                    country={country}
                  />
                </div>
                <div className="col-lg-4">
                  <AdditionalReferences />
                </div>
              </div>
            </CollapsibleFormGroup>

            <CollapsibleFormGroup
              key={`transportation-${isFormPopulated}`}
              title={t('declaration.p5.consignmentItemDrawer.transportation')}
              initialOpen={hasTransportData}
            >
              <div className="row">
                <div className="col-lg-12">
                  <TransitPassiveBorderTransportMeans
                    borderModeOfTransport={editorData?.borderModeOfTransport ?? ''}
                  />
                </div>
              </div>
            </CollapsibleFormGroup>
          </div>
        </form>
      </FormProvider>
    </LoadingOverlay>
  )
})

TransitConsignmentItemForm.defaultProps = {
  onSaveSuccess: undefined,
  fieldErrors: undefined,
}

export default TransitConsignmentItemForm
